import {inject, Injectable} from '@angular/core';
import {
    AssistanceApplicationType,
    CompanyCategory,
    CompanyCategoryClient,
    CompanyCategoryThresholdViewModel
} from './api.client';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {IPayeeCompanyCategoryAmount} from '../shared/interfaces/IPayeeCompanyCategoryAmount';
import {StateResetRegistry} from './state-reset-registry.service';

// 003-retire-ngrx (US3): the companyCategory slice moved off NgRx into this
// service. companyCategories is login-loaded reference data (SecurityService
// pushes it here) read by the company pages; the thresholds/amounts pair is
// the assistance-application flow's cross-component channel (create/edit ->
// form -> exceeds-threshold). Typed state also fixes the old drift where the
// slice was registered in the reducer map but missing from AppState — the
// companyCategories payload was always CompanyCategory[], not the
// CompanyCategoryThresholdViewModel[] its action props claimed.
interface CompanyCategorySliceState {
    categoryThresholds: CompanyCategoryThresholdViewModel[];
    currentPayeeCompanyCategoryAmounts: IPayeeCompanyCategoryAmount[];
    companyCategories: CompanyCategory[];
}

const initialState: CompanyCategorySliceState = {
    categoryThresholds: [],
    currentPayeeCompanyCategoryAmounts: [],
    companyCategories: []
};

@Injectable()
export class CompanyCategoryService {
    private _companyCategoryThresholdClient = inject(CompanyCategoryClient);

    private readonly _state$ = new BehaviorSubject<CompanyCategorySliceState>(initialState);

    // ---------------- Streams (former companyCategory selectors) ----------------
    readonly categoryThresholds$ = this.select((s) => s.categoryThresholds);
    readonly currentPayeeCompanyCategoryAmounts$ = this.select((s) => s.currentPayeeCompanyCategoryAmounts);
    readonly companyCategories$ = this.select((s) => s.companyCategories);

    constructor() {
        inject(StateResetRegistry).register(() => this._state$.next(initialState));
    }

    getCategoryThresholds(applicationType: AssistanceApplicationType = null): Observable<CompanyCategoryThresholdViewModel[]> {
        return this._companyCategoryThresholdClient.getCompanyCategoryThresholds(applicationType);
    }

    // -------- state transitions (former companyCategory actions) ----------
    setCategoryThresholds(categoryThresholds: CompanyCategoryThresholdViewModel[]): void {
        this.patch({categoryThresholds});
    }

    setCurrentPayeeCompanyCategoryAmounts(amounts: IPayeeCompanyCategoryAmount[]): void {
        this.patch({currentPayeeCompanyCategoryAmounts: amounts});
    }

    setCompanyCategories(companyCategories: CompanyCategory[]): void {
        this.patch({companyCategories});
    }

    // ---------------- State plumbing ----------------
    private select<T>(project: (state: CompanyCategorySliceState) => T): Observable<T> {
        return this._state$.pipe(map(project), distinctUntilChanged());
    }

    private patch(partial: Partial<CompanyCategorySliceState>): void {
        this._state$.next({...this._state$.value, ...partial});
    }
}
